import React, { useContext, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Typography,
  Stack,
  Avatar,
  FormControl,
  Input,
  Button,
  Box,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OrderTableHead from './OrderTableHead';
import { CommonContext, GaintOFDOrdersContext } from '../../context';
import { delivery_type_id } from '../../utils/config';
import moment from 'moment/moment';
import { CallMade, LinkOutlined, LocalShipping, Person, RecordVoiceOverOutlined } from '@mui/icons-material';
import TableSkeleton from '../common/TableSkeleton';
import { Link, useNavigate } from 'react-router-dom';
import { AppUrl } from "../../utils/config";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { FileCopy as FileCopyIcon } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function OrderTable() {
  const navigate = useNavigate();
  const { firstLoading, allData, allOrders, selected, setSelected, page, setPage, rowsPerPage, setRowsPerPage, viewDetail, onTracking, downLoadPod, onCalling, onRecording, activeTab } = useContext(GaintOFDOrdersContext);
  const { getOrderStatus, getOrderStatusColor, user } = useContext(CommonContext);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [copied, setCopied] = useState(false);

  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    console.log(event.target.value);
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = allOrders.map((order) => order.id);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // const sortedRows = orders.slice().sort(getComparator(order, orderBy));

  const sortedRows = useMemo(() => {
    return [...allOrders].sort((a, b) => {
      if (order === 'asc') {
        return a[orderBy] > b[orderBy] ? 1 : -1;
      } else {
        return a[orderBy] < b[orderBy] ? 1 : -1;
      }
    });
  }, [allOrders, orderBy, order]);

  const renderPODCell = ({ row, user, AppUrl, handleChangePOD, downLoadPod }) => {
    // show only for status 8, 18, 12
    if (![8, 18, 12].includes(row?.order_status_id)) return 'NA';

    // case 1: POD file already exists → show download link
    if (row?.pod_file) {
      return (
        <Typography sx={{ color: '#004b8d', cursor: 'pointer' }} className='link-text'>
          <Link to={`${AppUrl}/download/${row?.pod_file}?pathName=orders/pod/`}>
            Download POD
          </Link>
        </Typography>
      );
    }

    // case 2: status = 8
    if (row?.order_status_id === 8) {
      // sub-case: courier in allowed list
      if ([1, 2, 3, 4, 5].includes(row?.courier_id)) {
        return user && user?.user_type !== 'Merchant' ? (
          <FormControl component="fieldset" title="Upload POD" sx={{ width: '100%' }}>
            <Input
              type="file"
              id={`fileInputPOD-${row?.id}`}
              style={{ display: 'none' }}
              name="dimension_file"
              onChange={(e) => handleChangePOD(e, row?.id)}
              inputProps={{ accept: '.png, .jpg, .jpeg, .pdf, .svg', capture: 'environment' }}
            />
            <label htmlFor={`fileInputPOD-${row?.id}`} style={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                component="span"
                size="small"
                startIcon={<CloudUploadIcon />}
                sx={{ textWrap: 'nowrap' }}
              >
                Upload POD
              </Button>
            </label>
          </FormControl>
        ) : (
          'NA'
        );
      }

      // sub-case: other couriers → click to download POD
      return (
        <Typography
          sx={{ color: '#004b8d', cursor: 'pointer' }}
          onClick={() => downLoadPod(row?.id)}
          className='link-text'
        >
          POD
        </Typography>
      );
    }

    return 'NA';
  };

  const handleChangePOD = (event, id) => {
    const { name, type, files } = event.target;
    if (type === 'file') {
      const formData = new FormData();
      formData.append('pod_file', files[0]);
      formData.append('order_id', id);
      // commonApi.post('orders/upload-pod', formData).then((res) => {
      //   if (res?.data?.s === 0) {
      //     enqueueSnackbar(res?.data?.msg, { variant: 'error' });
      //   } else {
      //     enqueueSnackbar(res?.data?.msg, { variant: 'success' });
      //   }
      // });
    }
  };
  const handleCopy = (value) => {
    navigator.clipboard.writeText(value); // Copying text to clipboard
    setCopied(true); // Setting copied state to true
    setTimeout(() => setCopied(false), 1000); // Resetting copied state after 1.5 seconds
  };
  return (
    <div className='table-component'>
      {firstLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">
            <strong>{selected.length}</strong> Orders Selected
          </Typography>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 150, 200, 250]}
            component="div"
            count={allData?.TotalRows || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      ) : null}
      <TableContainer>
        <Table size="small">
          <OrderTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            selected={selected}
            onSelectAllClick={handleSelectAllClick}
            rowCount={sortedRows.length}
          />
          <TableBody>
            {!firstLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ p: 0 }}>
                  <TableSkeleton rows={10} columns={8} />
                </TableCell>
              </TableRow>
            ) : sortedRows.length > 0 ?
              sortedRows.map((row) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow hover key={row.id} selected={isItemSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={() => handleSelect(row.id)}
                        className="custom-checkbox"
                      />
                    </TableCell>
                    {user?.user_type !== "Merchant" && (
                      <TableCell align="left">{row?.merchant?.company_name}</TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={row?.service_type || 'N/A'}
                        color={row?.service_type === 'B2C' ? 'primary' : 'secondary'}
                        size="small"
                        className="cutom-table-chip"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography className='link-text' onClick={() => viewDetail(row.id)}>
                        {row.order_no} <LinkOutlined sx={{ fontSize: '0.75rem', verticalAlign: 'middle', marginLeft: '3px' }} />
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#004d40' }}>
                          <Person sx={{ fontSize: 18, color: '#fff' }} />
                        </Avatar>
                        <Typography className='custome-typography'>
                          {row.customer_name}<br />
                          <small>{row.customer_city}<br />{row.customer_state}</small>
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#004d40' }}>
                          <LocalShipping sx={{ fontSize: 18, color: '#fff' }} />
                        </Avatar>
                        <Stack spacing={0.3}>
                          <Typography className='copy-section'>
                            <Chip
                              label={row?.awb_no || 'N/A'}
                              size="small"
                              className="awb-chip cutom-table-chip"
                            />
                            <Tooltip title={copied ? "Copied" : "Copy AWB Number"} placement="top" arrow>
                              <IconButton onClick={() => handleCopy(row.awb_no)}>
                                <FileCopyIcon />
                              </IconButton>
                            </Tooltip>
                          </Typography>

                          <Typography sx={{ fontSize: '0.8rem' }}>
                            <strong>{row?.courier?.brand_name}</strong>
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Chip
                          variant="outlined"
                          label={getOrderStatus(row.order_status_id)}
                          color={getOrderStatusColor(row.order_status_id)}
                          size="small"
                          className="cutom-table-chip"
                          onClick={() => onTracking(row.id)}
                        />
                      </div>
                      {row.order_status_id === 21 && (
                        <div>
                          <Chip
                            label="Re-ship"
                            color="info"
                            size="small"
                            className="cutom-table-chip"
                            sx={{ mt: 1, cursor: 'pointer' }}
                            onClick={() => navigate('/orders/create-b2c', { state: row })}
                          />
                        </div>
                      )}
                    </TableCell>

                    <TableCell>
                      {row?.merchant_warehouse?.contact_person}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={delivery_type_id[row.delivery_type_id]}
                        color={delivery_type_id[row.delivery_type_id] === 'COD' ? 'info' : 'success'}
                        className="cutom-table-chip"
                        size="small" />
                      <br />
                      <small>{row?.delivery_type_id === 1 ? row?.product_cod_value : row?.product_invoice_value}</small>
                    </TableCell>
                    <TableCell>
                      <Typography className='custome-typography' sx={{ whiteSpace: 'nowrap' }}>
                        {row.created_at ? moment(row.created_at).format('DD-MM-YYYY') : 'NA'}<br />
                        <small>{row?.user?.name}</small>
                      </Typography>
                    </TableCell>
                    {activeTab !== 21 && (
                      <TableCell>
                        {renderPODCell({ row, user, AppUrl, handleChangePOD, downLoadPod })}
                        {row?.is_ivr === 1 && (
                          <Chip
                            variant="outlined"
                            label={'Recording'}
                            color={'secondary'}
                            size="small"
                            className="cutom-table-chip"
                            icon={<RecordVoiceOverOutlined />}
                            sx={{ my: 1, cursor: 'pointer' }}
                            onClick={() => onRecording(row)}
                          />
                        )}

                        {user && user?.user_type !== "Merchant" && ![88].includes(row?.order_status_id) && (
                          <Chip
                            variant="outlined"
                            label={'Click to Call'}
                            color={'default'}
                            size="small"
                            className="cutom-table-chip"
                            icon={<CallMade fontSize="small" />}
                            onClick={() => onCalling(row)}
                          />
                        )}
                      </TableCell>
                    )}

                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No orders found
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
